import React, { useState } from "react";
import "../App.css";
import {
  FaUserTie,
  FaPhoneAlt,
  FaEnvelope,
  FaBriefcase,
  FaTimes,
  FaMapMarkerAlt,
} from "react-icons/fa";
import logo from "../images/logo.png";
import { useCareModal } from "../context/CareModalContext";

const JoinTeamModal = () => {
  const { isJoinTeamOpen, closeJoinTeam } = useCareModal();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    role: "",
    experience: "",
  });

  const [errors, setErrors] = useState({});

  if (!isJoinTeamOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};

    if (!form.name.trim()) newErrors.name = "Full name is required.";
    if (!form.email.trim()) {
      newErrors.email = "Email address is required.";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email.trim())) {
        newErrors.email = "Please enter a valid email address.";
      }
    }
    if (!form.role) newErrors.role = "Please select the role you're applying for.";
    if (!form.experience.trim()) {
      newErrors.experience = "Please tell us about your experience.";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const response = await fetch("/api/send-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type: "joinTeam",
          payload: form,
        }),
      });

      if (!response.ok) {
        console.error("Failed to send application");
        alert(
          "Sorry, something went wrong sending your application. Please try again."
        );
        return;
      }

      alert("Thank you – your application has been sent.");
      closeJoinTeam();
      setForm({
        name: "",
        email: "",
        phone: "",
        location: "",
        role: "",
        experience: "",
      });
      setErrors({});
    } catch (err) {
      console.error("Error sending application:", err);
      alert(
        "Sorry, something went wrong sending your application. Please try again."
      );
    }
  };

  return (
    <div className="modal-backdrop" onClick={closeJoinTeam}>
      <div
        className="modal join-team-modal modal-animate"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="modal-close-btn"
          onClick={closeJoinTeam}
          aria-label="Close"
        >
          <FaTimes />
        </button>

        <div className="book-care-header">
          <div className="book-care-logo-wrap">
            <img
              src={logo}
              alt="Ndils Daily Care Logo"
              className="book-care-logo"
            />
          </div>
          <div className="book-care-title-block">
            <h3>Join the Ndils Daily Care Team</h3>
            <p>
              We’re always looking for compassionate, reliable carers and
              support workers. Share your details and we’ll be in touch.
            </p>
          </div>
        </div>

        <form className="book-care-form" onSubmit={handleSubmit} noValidate>
          <div className="book-care-row">
            <div
              className={`book-care-field ${
                errors.name ? "field-error" : ""
              }`}
            >
              <label>
                <span className="field-icon">
                  <FaUserTie />
                </span>
                Full Name
              </label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                required
              />
              {errors.name && <p className="error-text">{errors.name}</p>}
            </div>

            <div
              className={`book-care-field ${
                errors.email ? "field-error" : ""
              }`}
            >
              <label>
                <span className="field-icon">
                  <FaEnvelope />
                </span>
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
              />
              {errors.email && <p className="error-text">{errors.email}</p>}
            </div>
          </div>

          <div className="book-care-row">
            <div className="book-care-field">
              <label>
                <span className="field-icon">
                  <FaPhoneAlt />
                </span>
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="+44 ..."
              />
            </div>

            <div className="book-care-field">
              <label>
                <span className="field-icon">
                  <FaMapMarkerAlt />
                </span>
                Location (Town / Postcode)
              </label>
              <input
                type="text"
                name="location"
                value={form.location}
                onChange={handleChange}
                placeholder="E.g. NP18 / Newport"
              />
            </div>
          </div>

          <div
            className={`book-care-field ${
              errors.role ? "field-error" : ""
            }`}
          >
            <label>
              <span className="field-icon">
                <FaBriefcase />
              </span>
              Role You’re Interested In
            </label>
            <select
              name="role"
              value={form.role}
              onChange={handleChange}
              required
            >
              <option value="">Please select</option>
              <option value="carer">Carer / Support Worker</option>
              <option value="senior-carer">Senior Carer</option>
              <option value="nurse">Nurse / Clinical Role</option>
              <option value="other">Other Role</option>
            </select>
            {errors.role && <p className="error-text">{errors.role}</p>}
          </div>

          <div
            className={`book-care-field ${
              errors.experience ? "field-error" : ""
            }`}
          >
            <label>Relevant Experience & Qualifications</label>
            <textarea
              name="experience"
              rows="4"
              value={form.experience}
              onChange={handleChange}
              placeholder="Tell us about your care background, qualifications, and availability..."
              required
            />
            {errors.experience && (
              <p className="error-text">{errors.experience}</p>
            )}
          </div>

          <div className="book-care-footer">
            <p className="book-care-note">
              By submitting this form you consent to Ndils Daily Care storing
              your details for recruitment purposes.
            </p>
            <button type="submit" className="btn btn-primary">
              Submit Application
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JoinTeamModal;
